import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAppAuth } from './AuthContext';

const PanelContext = createContext();

const CACHE_KEY = 'admin_events_cache';
const THUMB_CACHE_KEY = 'admin_thumbtests_cache';

export function PanelProvider({ children }) {
  const { getToken } = useAppAuth();

  const [events, setEvents] = useState(() => {
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });
  const [loading, setLoading] = useState(!events.length);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  const [thumbTests, setThumbTests] = useState(() => {
    try {
      const cached = sessionStorage.getItem(THUMB_CACHE_KEY);
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });
  const [thumbLoading, setThumbLoading] = useState(!thumbTests.length);

  const authFetch = useCallback(async (method, body) => {
    const token = await getToken();
    const res = await fetch('/api/admin/events', {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
    return data;
  }, [getToken]);

  const thumbAuthFetch = useCallback(async (method, body) => {
    const token = await getToken();
    const res = await fetch('/api/thumbnail-tests', {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
    return data;
  }, [getToken]);

  const fetchThumbTests = useCallback(async () => {
    try {
      const data = await thumbAuthFetch('GET');
      if (mountedRef.current) {
        setThumbTests(data.tests || []);
        try { sessionStorage.setItem(THUMB_CACHE_KEY, JSON.stringify(data.tests || [])); } catch {}
      }
    } catch {} finally {
      if (mountedRef.current) setThumbLoading(false);
    }
  }, [thumbAuthFetch]);

  const fetchEvents = useCallback(async () => {
    try {
      const data = await authFetch('GET');
      const safe = (data.events || []).map(e => {
        const out = {};
        for (const [k, v] of Object.entries(e)) {
          out[k] = (v && typeof v === 'object' && !Array.isArray(v)) ? String(v) : v;
        }
        return out;
      });
      if (mountedRef.current) {
        setEvents(safe);
        setError(null);
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(safe)); } catch {}
      }
    } catch (err) {
      if (mountedRef.current) setError(err.message);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    mountedRef.current = true;
    fetchEvents();
    fetchThumbTests();
    const interval = setInterval(fetchEvents, 30000);
    return () => {
      mountedRef.current = false;
      clearInterval(interval);
    };
  }, [fetchEvents, fetchThumbTests]);

  return (
    <PanelContext.Provider value={{ events, loading, error, authFetch, refreshEvents: fetchEvents, thumbTests, thumbLoading, thumbAuthFetch, refreshThumbTests: fetchThumbTests }}>
      {children}
    </PanelContext.Provider>
  );
}

export function usePanel() {
  const context = useContext(PanelContext);
  if (!context) {
    throw new Error('usePanel must be used within a PanelProvider');
  }
  return context;
}
